/* Branded A4-style invoice. Rendered inside .print-only > .print-page so it can be
   printed / exported to PDF, and shown on screen by wrapping in .pdf-preview-wrap. */
const gbp = (n) => `£${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '');

export default function InvoiceDocument({ invoice, business }) {
  if (!invoice) return null;
  const accent = business?.brand_color || '#FF5A1F';
  const items = (invoice.items || []).filter((it) => it.description || it.qty || it.unit_price);
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const vatAmount = invoice.vat_amount != null ? invoice.vat_amount : subtotal * ((invoice.vat_rate || 0) / 100);
  const total = invoice.total != null ? invoice.total : subtotal + vatAmount;
  const amountPaid = invoice.amount_paid || 0;
  const balance = invoice.balance_due != null ? invoice.balance_due : total - amountPaid;
  const deposit = invoice.deposit_amount || 0;
  const isPaid = invoice.status === 'paid';

  return (
    <div className="print-only" data-testid="invoice-document">
      <div className="print-page relative">
        {isPaid && (
          <div className="absolute right-6 top-16 rotate-[-12deg] border-4 border-emerald-600 text-emerald-600 font-display font-semibold uppercase text-3xl tracking-widest px-4 py-1 opacity-80">
            Paid
          </div>
        )}
        <header className="flex items-start justify-between border-b-2 pb-4" style={{ borderColor: accent }}>
          <div className="flex items-start gap-4">
            {business?.logo_data ? (
              <img src={business.logo_data} alt="Logo" className="max-h-20 max-w-[160px] object-contain" />
            ) : null}
            <div>
              <p className="font-display font-semibold text-2xl uppercase tracking-tight">
                {business?.business_name || 'TradesMate User'}
              </p>
              {business?.business_address && (
                <p className="whitespace-pre-line text-sm text-slate-700 mt-1">{business.business_address}</p>
              )}
              <p className="text-sm text-slate-700 mt-1">
                {business?.business_phone && <span>{business.business_phone}</span>}
                {business?.business_phone && business?.business_email && <span> · </span>}
                {business?.business_email && <span>{business.business_email}</span>}
              </p>
              {business?.business_vat_number && (
                <p className="text-xs text-slate-600 mt-1">VAT no: {business.business_vat_number}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="font-display font-semibold text-3xl uppercase tracking-tight" style={{ color: accent }}>INVOICE</p>
            {invoice.invoice_number && <p className="font-mono text-sm mt-1">No. {invoice.invoice_number}</p>}
            <p className="font-mono text-xs text-slate-600 mt-1">Issued: {fmtDate(invoice.created_at)}</p>
            {invoice.due_date && <p className="font-mono text-xs text-slate-600">Due: {fmtDate(invoice.due_date)}</p>}
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Bill to</p>
            <p className="font-display font-bold text-lg mt-1">{invoice.customer_name || '—'}</p>
            {invoice.customer_address && <p className="text-sm whitespace-pre-line mt-1">{invoice.customer_address}</p>}
            {invoice.customer_phone && <p className="text-sm">{invoice.customer_phone}</p>}
            {invoice.customer_email && <p className="text-sm">{invoice.customer_email}</p>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</p>
            <p className="font-display font-bold text-lg mt-1">{invoice.title || 'Invoice'}</p>
          </div>
        </section>

        {items.length > 0 && (
          <section className="mt-6">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-y border-slate-200">
                  <th className="text-left py-2 font-display uppercase text-xs tracking-wider">Description</th>
                  <th className="text-right py-2 w-16 font-display uppercase text-xs tracking-wider">Qty</th>
                  <th className="text-left py-2 w-16 font-display uppercase text-xs tracking-wider">Unit</th>
                  <th className="text-right py-2 w-24 font-display uppercase text-xs tracking-wider">£/unit</th>
                  <th className="text-right py-2 w-28 font-display uppercase text-xs tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const lt = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
                  return (
                    <tr key={i} className="border-b border-slate-300">
                      <td className="py-2">{it.description || '-'}</td>
                      <td className="py-2 text-right font-mono">{it.qty || 0}</td>
                      <td className="py-2 font-mono text-xs">{it.unit || ''}</td>
                      <td className="py-2 text-right font-mono">{gbp(parseFloat(it.unit_price) || 0)}</td>
                      <td className="py-2 text-right font-mono font-bold">{gbp(lt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        <section className="grid grid-cols-2 gap-6 mt-6">
          <div>
            {invoice.payment_details && (
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1 font-semibold">How to pay</p>
                <p className="text-sm whitespace-pre-line">{invoice.payment_details}</p>
              </div>
            )}
            {invoice.notes && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1 font-semibold">Notes</p>
                <p className="text-sm whitespace-pre-line">{invoice.notes}</p>
              </div>
            )}
          </div>
          <div>
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1">Subtotal (excl. VAT)</td><td className="text-right font-mono font-bold">{gbp(subtotal)}</td></tr>
                <tr><td className="py-1 text-slate-600">VAT ({invoice.vat_rate}%)</td><td className="text-right font-mono">{gbp(vatAmount)}</td></tr>
                <tr className="border-t border-slate-200"><td className="py-2 font-display font-semibold text-lg uppercase">Total</td><td className="text-right font-mono font-semibold text-xl" style={{ color: accent }}>{gbp(total)}</td></tr>
                {amountPaid > 0 && (
                  <tr><td className="py-1 text-emerald-700">Paid to date</td><td className="text-right font-mono text-emerald-700">− {gbp(amountPaid)}</td></tr>
                )}
                {amountPaid > 0 && (
                  <tr className="border-t border-slate-300"><td className="py-1 font-display font-bold uppercase">Balance due</td><td className="text-right font-mono font-semibold">{gbp(balance)}</td></tr>
                )}
                {deposit > 0 && amountPaid <= 0 && (
                  <tr className="border-t border-slate-300"><td className="py-1 text-slate-600">Deposit requested</td><td className="text-right font-mono">{gbp(deposit)}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-slate-300 text-xs text-slate-500 font-mono">
          <p>Payment due by {fmtDate(invoice.due_date)}. Thank you for your business.</p>
          <p className="mt-2">Generated with TradesMate UK · tradesmateuk.co.uk</p>
        </footer>
      </div>
    </div>
  );
}
